import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ArrowLeftIcon,
  DocumentTextIcon,
  CurrencyRupeeIcon,
  UserIcon,
  CalendarIcon,
} from "@heroicons/react/outline";
import {
  getRefundStatusThunk,
  approveRefundThunk,
  completeRefundThunk,
  updateRefundStatusThunk,
} from "../../../store/thunks/jewelleryPlan/cancellationThunks";
import {
  selectCurrentRefund,
  selectRefundsLoading,
  selectRefundError,
  selectRefundActionLoading,
} from "../../../store/slices/jewelleryPlan/cancellationSlice";

const RefundDetails = () => {
  const { refundId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const refund = useSelector(selectCurrentRefund);
  const loading = useSelector(selectRefundsLoading);
  const error = useSelector(selectRefundError);
  const actionLoading = useSelector(selectRefundActionLoading);

  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showFailModal, setShowFailModal] = useState(false);
  const [transactionReference, setTransactionReference] = useState("");
  const [note, setNote] = useState("");
  const [failureReason, setFailureReason] = useState("");

  useEffect(() => {
    dispatch(getRefundStatusThunk(refundId));
  }, [dispatch, refundId]);

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: {
        bg: "bg-warning-light",
        text: "text-warning-dark",
        icon: ClockIcon,
        label: "Pending Approval",
      },
      APPROVED: {
        bg: "bg-primary-light",
        text: "text-primary-dark",
        icon: CheckCircleIcon,
        label: "Approved",
      },
      PROCESSING: {
        bg: "bg-secondary-light",
        text: "text-secondary-dark",
        icon: ClockIcon,
        label: "Processing",
      },
      COMPLETED: {
        bg: "bg-success-light",
        text: "text-success-dark",
        icon: CheckCircleIcon,
        label: "Completed",
      },
      FAILED: {
        bg: "bg-danger-light",
        text: "text-danger-dark",
        icon: XCircleIcon,
        label: "Failed",
      },
      CANCELLED: {
        bg: "bg-neutral-light",
        text: "text-neutral-dark",
        icon: XCircleIcon,
        label: "Cancelled",
      },
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    const Icon = config.icon;

    return (
      <span
        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}
      >
        <Icon className="h-5 w-5 mr-2" />
        {config.label}
      </span>
    );
  };

  const handleApprove = async () => {
    try {
      await dispatch(
        approveRefundThunk({
          refundRequestId: refundId,
          approvalData: { note },
        })
      ).unwrap();

      setShowApproveModal(false);
      setNote("");
      dispatch(getRefundStatusThunk(refundId));
    } catch (error) {
      alert("Failed to approve refund: " + error);
    }
  };

  const handleComplete = async () => {
    if (!transactionReference) {
      alert("Transaction reference is required");
      return;
    }

    try {
      await dispatch(
        completeRefundThunk({
          refundRequestId: refundId,
          completionData: { transactionReference, note },
        })
      ).unwrap();

      setShowCompleteModal(false);
      setTransactionReference("");
      setNote("");
      dispatch(getRefundStatusThunk(refundId));
    } catch (error) {
      alert("Failed to complete refund: " + error);
    }
  };

  const handleMarkFailed = async () => {
    if (!failureReason) {
      alert("Failure reason is required");
      return;
    }

    try {
      await dispatch(
        updateRefundStatusThunk({
          refundRequestId: refundId,
          statusData: {
            status: "FAILED",
            failureReason,
            notes: note,
          },
        })
      ).unwrap();

      setShowFailModal(false);
      setFailureReason("");
      setNote("");
      dispatch(getRefundStatusThunk(refundId));
    } catch (error) {
      alert("Failed to update status: " + error);
    }
  };

  if (loading && !refund) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-danger-light border border-danger rounded-lg p-6">
          <div className="flex items-start">
            <XCircleIcon className="h-6 w-6 text-danger mt-0.5 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-danger-dark">Error</h3>
              <p className="mt-2 text-sm text-danger-dark">{error}</p>
              <button
                onClick={() => navigate("/jewellery/cancellations/refunds")}
                className="mt-4 px-4 py-2 bg-danger text-white rounded-lg hover:bg-danger-dark"
              >
                Back to Refunds
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!refund) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <DocumentTextIcon className="h-12 w-12 text-neutral mx-auto mb-4" />
          <p className="text-neutral-dark">Refund request not found</p>
          <button
            onClick={() => navigate("/jewellery/cancellations/refunds")}
            className="mt-4 text-primary hover:text-primary-dark"
          >
            Back to Refunds
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate("/jewellery/cancellations/refunds")}
          className="flex items-center text-primary hover:text-primary-dark mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Refunds
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text">Refund Details</h1>
            <p className="mt-1 text-sm text-neutral-dark">
              Request #{refund.requestNumber}
            </p>
          </div>
          {getStatusBadge(refund.status)}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Amount Card */}
          <div className="bg-primary-light border border-primary rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-primary-dark">Refund Amount</p>
                <p className="text-3xl font-bold text-primary-dark mt-1">
                  {formatAmount(refund.amount)}
                </p>
              </div>
              <CurrencyRupeeIcon className="h-12 w-12 text-primary" />
            </div>
          </div>

          {/* Basic Details */}
          <div className="bg-background rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-text mb-4">
              Refund Information
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-neutral-dark">Plan Number</p>
                <p className="font-medium">
                  {refund.planId?.planNumber || "N/A"}
                </p>
              </div>
              <div>
                <p className="text-sm text-neutral-dark">Method</p>
                <p className="font-medium">{refund.method}</p>
              </div>
              <div>
                <p className="text-sm text-neutral-dark">Reason</p>
                <p className="font-medium">{refund.reason}</p>
              </div>
              <div>
                <p className="text-sm text-neutral-dark">Branch</p>
                <p className="font-medium">{refund.branchCode || "N/A"}</p>
              </div>
              <div>
                <p className="text-sm text-neutral-dark">Requested Date</p>
                <p className="font-medium">{formatDate(refund.requestedAt)}</p>
              </div>
              <div>
                <p className="text-sm text-neutral-dark">Last Updated</p>
                <p className="font-medium">{formatDate(refund.updatedAt)}</p>
              </div>
            </div>
          </div>

          {/* Account Details */}
          {refund.accountDetails && (
            <div className="bg-background rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-text mb-4">
                Account Details
              </h2>
              <div className="space-y-3">
                {refund.method === "BANK_TRANSFER" && (
                  <>
                    <div>
                      <p className="text-sm text-neutral-dark">
                        Account Number
                      </p>
                      <p className="font-medium font-mono">
                        {refund.accountDetails.accountNumber || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-neutral-dark">IFSC Code</p>
                      <p className="font-medium font-mono">
                        {refund.accountDetails.ifscCode || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-neutral-dark">Account Name</p>
                      <p className="font-medium">
                        {refund.accountDetails.accountName || "N/A"}
                      </p>
                    </div>
                  </>
                )}
                {refund.method === "UPI" && (
                  <div>
                    <p className="text-sm text-neutral-dark">UPI ID</p>
                    <p className="font-medium font-mono">
                      {refund.accountDetails.upiId || "N/A"}
                    </p>
                  </div>
                )}
                {refund.method === "CHEQUE" && (
                  <div>
                    <p className="text-sm text-neutral-dark">Cheque Number</p>
                    <p className="font-medium font-mono">
                      {refund.accountDetails.chequeNumber || "N/A"}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Processing Details */}
          {(refund.approvedBy ||
            refund.processedBy ||
            refund.transactionReference) && (
            <div className="bg-background rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-text mb-4">
                Processing Details
              </h2>
              <div className="space-y-3">
                {refund.approvedBy && (
                  <div>
                    <p className="text-sm text-neutral-dark">Approved By</p>
                    <p className="font-medium">
                      {refund.approvedBy.name} on{" "}
                      {formatDate(refund.approvedAt)}
                    </p>
                  </div>
                )}
                {refund.processedBy && (
                  <div>
                    <p className="text-sm text-neutral-dark">Processed By</p>
                    <p className="font-medium">
                      {refund.processedBy.name} on{" "}
                      {formatDate(refund.processedAt)}
                    </p>
                  </div>
                )}
                {refund.completedAt && (
                  <div>
                    <p className="text-sm text-neutral-dark">Completed Date</p>
                    <p className="font-medium">
                      {formatDate(refund.completedAt)}
                    </p>
                  </div>
                )}
                {refund.transactionReference && (
                  <div>
                    <p className="text-sm text-neutral-dark">
                      Transaction Reference
                    </p>
                    <p className="font-medium font-mono">
                      {refund.transactionReference}
                    </p>
                  </div>
                )}
                {refund.bankReference && (
                  <div>
                    <p className="text-sm text-neutral-dark">Bank Reference</p>
                    <p className="font-medium font-mono">
                      {refund.bankReference}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Failure Details */}
          {refund.status === "FAILED" && (
            <div className="bg-danger-light border border-danger rounded-lg p-6">
              <h2 className="text-lg font-semibold text-danger-dark mb-4">
                Failure Information
              </h2>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-danger-dark">Failure Reason</p>
                  <p className="font-medium text-text">
                    {refund.failureReason || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-danger-dark">Failure Count</p>
                  <p className="font-medium text-text">
                    {refund.failureCount || 0} attempt(s)
                  </p>
                </div>
                {refund.lastFailureAt && (
                  <div>
                    <p className="text-sm text-danger-dark">Last Failure</p>
                    <p className="font-medium text-text">
                      {formatDate(refund.lastFailureAt)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          {(refund.internalNotes || refund.customerNotes) && (
            <div className="bg-background rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-text mb-4">Notes</h2>
              {refund.internalNotes && (
                <div className="mb-4">
                  <p className="text-sm text-neutral-dark mb-1">
                    Internal Notes
                  </p>
                  <div className="bg-neutral-light rounded p-3">
                    <p className="text-sm text-text whitespace-pre-wrap">
                      {refund.internalNotes}
                    </p>
                  </div>
                </div>
              )}
              {refund.customerNotes && (
                <div>
                  <p className="text-sm text-neutral-dark mb-1">
                    Customer Notes
                  </p>
                  <div className="bg-neutral-light rounded p-3">
                    <p className="text-sm text-text whitespace-pre-wrap">
                      {refund.customerNotes}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Actions */}
          <div className="bg-background rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-text mb-4">Actions</h2>
            <div className="space-y-3">
              {refund.status === "PENDING" && (
                <button
                  onClick={() => setShowApproveModal(true)}
                  disabled={actionLoading}
                  className="w-full px-4 py-2 bg-success text-white rounded-lg hover:bg-success-dark disabled:opacity-50 flex items-center justify-center"
                >
                  <CheckCircleIcon className="h-5 w-5 mr-2" />
                  Approve Refund
                </button>
              )}
              {(refund.status === "APPROVED" ||
                refund.status === "PROCESSING") && (
                <button
                  onClick={() => setShowCompleteModal(true)}
                  disabled={actionLoading}
                  className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 flex items-center justify-center"
                >
                  <CheckCircleIcon className="h-5 w-5 mr-2" />
                  Mark as Completed
                </button>
              )}
              {(refund.status === "APPROVED" ||
                refund.status === "PROCESSING") && (
                <button
                  onClick={() => setShowFailModal(true)}
                  disabled={actionLoading}
                  className="w-full px-4 py-2 bg-danger text-white rounded-lg hover:bg-danger-dark disabled:opacity-50 flex items-center justify-center"
                >
                  <XCircleIcon className="h-5 w-5 mr-2" />
                  Mark as Failed
                </button>
              )}
              <button
                onClick={() => navigate(`/jewellery/${refund.planId}`)}
                className="w-full px-4 py-2 border border-primary text-primary rounded-lg hover:bg-primary-light"
              >
                View Plan
              </button>
              <button
                onClick={() => window.print()}
                className="w-full px-4 py-2 border border-neutral-light rounded-lg hover:bg-neutral-light"
              >
                Print Details
              </button>
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-background rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-text mb-4">Timeline</h2>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center">
                    <CalendarIcon className="h-4 w-4 text-white" />
                  </div>
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium text-text">Requested</p>
                  <p className="text-xs text-neutral-dark">
                    {formatDate(refund.requestedAt)}
                  </p>
                </div>
              </div>

              {refund.approvedAt && (
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 rounded-full bg-success flex items-center justify-center">
                      <CheckCircleIcon className="h-4 w-4 text-white" />
                    </div>
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-sm font-medium text-text">Approved</p>
                    <p className="text-xs text-neutral-dark">
                      {formatDate(refund.approvedAt)}
                    </p>
                    {refund.approvedBy && (
                      <p className="text-xs text-neutral-dark">
                        by {refund.approvedBy.name}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {refund.processedAt && (
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center">
                      <ClockIcon className="h-4 w-4 text-white" />
                    </div>
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-sm font-medium text-text">Processing</p>
                    <p className="text-xs text-neutral-dark">
                      {formatDate(refund.processedAt)}
                    </p>
                  </div>
                </div>
              )}

              {refund.completedAt && (
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 rounded-full bg-success flex items-center justify-center">
                      <CheckCircleIcon className="h-4 w-4 text-white" />
                    </div>
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-sm font-medium text-text">Completed</p>
                    <p className="text-xs text-neutral-dark">
                      {formatDate(refund.completedAt)}
                    </p>
                  </div>
                </div>
              )}

              {refund.status === "FAILED" && refund.lastFailureAt && (
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 rounded-full bg-danger flex items-center justify-center">
                      <XCircleIcon className="h-4 w-4 text-white" />
                    </div>
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-sm font-medium text-text">Failed</p>
                    <p className="text-xs text-neutral-dark">
                      {formatDate(refund.lastFailureAt)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Approve Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-background rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-text mb-4">
              Approve Refund
            </h3>
            <div className="mb-4">
              <p className="text-sm text-neutral-dark">Request Number</p>
              <p className="font-medium">{refund.requestNumber}</p>
            </div>
            <div className="mb-4">
              <p className="text-sm text-neutral-dark">Amount</p>
              <p className="text-lg font-bold text-text">
                {formatAmount(refund.amount)}
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-text mb-1">
                Approval Note (Optional)
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-neutral-light rounded-lg focus:ring-primary focus:border-primary"
                placeholder="Add any notes..."
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowApproveModal(false);
                  setNote("");
                }}
                disabled={actionLoading}
                className="px-4 py-2 border border-neutral-light rounded-lg hover:bg-neutral-light disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                disabled={actionLoading}
                className="px-4 py-2 bg-success text-white rounded-lg hover:bg-success-dark disabled:opacity-50"
              >
                {actionLoading ? "Approving..." : "Approve"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Complete Modal */}
      {showCompleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-background rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-text mb-4">
              Complete Refund
            </h3>
            <div className="mb-4">
              <p className="text-sm text-neutral-dark">Request Number</p>
              <p className="font-medium">{refund.requestNumber}</p>
            </div>
            <div className="mb-4">
              <p className="text-sm text-neutral-dark">Amount</p>
              <p className="text-lg font-bold text-text">
                {formatAmount(refund.amount)}
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-text mb-1">
                Transaction Reference *
              </label>
              <input
                type="text"
                value={transactionReference}
                onChange={(e) => setTransactionReference(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-light rounded-lg focus:ring-primary focus:border-primary"
                placeholder="Enter transaction reference..."
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-text mb-1">
                Completion Note (Optional)
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-neutral-light rounded-lg focus:ring-primary focus:border-primary"
                placeholder="Add any notes..."
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowCompleteModal(false);
                  setTransactionReference("");
                  setNote("");
                }}
                disabled={actionLoading}
                className="px-4 py-2 border border-neutral-light rounded-lg hover:bg-neutral-light disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleComplete}
                disabled={actionLoading || !transactionReference}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50"
              >
                {actionLoading ? "Completing..." : "Mark as Completed"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fail Modal */}
      {showFailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-background rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-text mb-4">
              Mark Refund as Failed
            </h3>
            <div className="mb-4">
              <p className="text-sm text-neutral-dark">Request Number</p>
              <p className="font-medium">{refund.requestNumber}</p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-text mb-1">
                Failure Reason *
              </label>
              <textarea
                value={failureReason}
                onChange={(e) => setFailureReason(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-neutral-light rounded-lg focus:ring-primary focus:border-primary"
                placeholder="Explain why the refund failed..."
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-text mb-1">
                Additional Notes (Optional)
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-neutral-light rounded-lg focus:ring-primary focus:border-primary"
                placeholder="Add any notes..."
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowFailModal(false);
                  setFailureReason("");
                  setNote("");
                }}
                disabled={actionLoading}
                className="px-4 py-2 border border-neutral-light rounded-lg hover:bg-neutral-light disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleMarkFailed}
                disabled={actionLoading || !failureReason}
                className="px-4 py-2 bg-danger text-white rounded-lg hover:bg-danger-dark disabled:opacity-50"
              >
                {actionLoading ? "Updating..." : "Mark as Failed"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RefundDetails;
